import React, { useEffect, useMemo, useState } from 'react';

import { BeneficiariosTable } from '../../../../components';
import PersonFormActionButtons from '../../../../components/pagesComponents/vidaPageComponents/PersonFormActionButtons';
import ProponentBeneficiarioFormSection from '../../../../components/pagesComponents/vidaPageComponents/ProponentBeneficiarioFormSection';
import ProponentInconsistencyModal from '../../../../components/pagesComponents/vidaPageComponents/modal/ProponentInconsistencyModal';
import { usePersonFormWizard } from '../../../../hooks/vida/usePersonFormWizard';
import type { ProponentBeneficiarioFormValues } from '../../../../types/vida/cadastralMaintenance/beneficiarioFormTypes';
import type { PersonFormSection } from '../../../../types/vida/cadastralMaintenance/personFormTypes';
import type { ProponentFormValues } from '../../../../types/vida/cadastralMaintenance/proponentFormTypes';
import type {
  BeneficiaryInclusionItem,
  ProponentDataReturn,
} from '../../../../types/vida/inclusionProponent/inclusionProponentType';
import type { BeneficiarioTableItem } from '../../../../types/vida/table/beneficiariosTableType';
import {
  createEmptyProponentBeneficiarioFormValues,
  mapProponentBeneficiarioFormToInclusionItem,
} from '../../../../utils/beneficiarioFormUtils';
import {
  isProponentInconsistencyError,
  parseProponentInconsistenciesFromError,
} from '../../../../utils/inclusionProponentUtil';
import { resolveParentescoLabel } from '../../../../utils/parentescoUtils';
import ProponentFormSection from './ProponentFormSection';

type BeneficiariosScreen = 'list' | 'detail' | null;
type ProponentWizardStep = PersonFormSection | 'beneficiarios';

type NewProponentFormProps = {
  isSaving: boolean;
  formKey: number;
  onSave: (
    values: ProponentFormValues,
    beneficiarios: BeneficiaryInclusionItem[],
  ) => Promise<void>;
  onCancel: () => void;
};

const mapProponentBeneficiariosToTableRows = (
  beneficiarios: ProponentBeneficiarioFormValues[],
): BeneficiarioTableItem[] => beneficiarios.map((item, index) => ({
  id: item.idBeneficiario || `proponente-benef-${index}`,
  nomeBeneficiario: item.nmBeneficiario.trim() || '-',
  codTipoParentesco: item.cdParentesco.trim() || '-',
  parentesco: resolveParentescoLabel(item.cdParentesco),
  percentual: Number(item.pcParticipacao.replace(',', '.')) || 0,
  codBneficiario: item.idBeneficiario || null,
  source: {
    nomeBeneficiario: item.nmBeneficiario,
    codTipoParentesco: item.cdParentesco,
    percentualPart: Number(item.pcParticipacao.replace(',', '.')) || 0,
  },
}));

const NewProponentForm: React.FC<NewProponentFormProps> = ({
  isSaving,
  formKey,
  onSave,
  onCancel,
}) => {
  const [isValid, setIsValid] = useState(false);
  const [draftValues, setDraftValues] = useState<ProponentFormValues | null>(null);
  const [restoreValues, setRestoreValues] = useState<ProponentFormValues | undefined>(undefined);
  const [formSectionKey, setFormSectionKey] = useState(0);
  const [isInconsistencyModalOpen, setIsInconsistencyModalOpen] = useState(false);
  const [inconsistencies, setInconsistencies] = useState<ProponentDataReturn[]>([]);
  const [beneficiarios, setBeneficiarios] = useState<ProponentBeneficiarioFormValues[]>([]);
  const [beneficiariosScreen, setBeneficiariosScreen] = useState<BeneficiariosScreen>(null);
  const [selectedBeneficiarioIndex, setSelectedBeneficiarioIndex] = useState<number | null>(null);
  const [beneficiarioDraft, setBeneficiarioDraft] = useState<ProponentBeneficiarioFormValues | null>(null);
  const [isBeneficiarioValid, setIsBeneficiarioValid] = useState(false);
  const [formSection, setFormSection] = useState<PersonFormSection>('personData');
  const wizardFormKey = `${formKey}-${formSectionKey}`;
  const {
    sectionControlRef,
    onActiveSectionChange: onWizardSectionChange,
  } = usePersonFormWizard(wizardFormKey);

  const isBeneficiariosListActive = beneficiariosScreen === 'list';
  const isBeneficiariosDetailActive = beneficiariosScreen === 'detail';
  const beneficiariosRows = useMemo(
    () => mapProponentBeneficiariosToTableRows(beneficiarios),
    [beneficiarios],
  );

  const currentStep: ProponentWizardStep = (isBeneficiariosListActive || isBeneficiariosDetailActive)
    ? 'beneficiarios'
    : formSection;

  const isWizardLastSection = !isBeneficiariosDetailActive && currentStep === 'beneficiarios';

  const onActiveSectionChange = (section: PersonFormSection) => {
    setFormSection(section);
    onWizardSectionChange(section);
  };

  useEffect(() => {
    setDraftValues(null);
    setRestoreValues(undefined);
    setFormSectionKey(0);
    setFormSection('personData');
    setIsValid(false);
    setIsInconsistencyModalOpen(false);
    setInconsistencies([]);
    setBeneficiarios([]);
    setBeneficiariosScreen(null);
    setSelectedBeneficiarioIndex(null);
    setBeneficiarioDraft(null);
    setIsBeneficiarioValid(false);
  }, [formKey]);

  useEffect(() => {
    setFormSection('personData');
  }, [wizardFormKey]);

  const resetBeneficiariosScreen = (options?: { clearDraft?: boolean }) => {
    setBeneficiariosScreen(null);
    if (options?.clearDraft !== false) {
      setSelectedBeneficiarioIndex(null);
      setBeneficiarioDraft(null);
      setIsBeneficiarioValid(false);
    }
  };

  const handleSave = async () => {
    if (!draftValues) {
      return;
    }

    try {
      await onSave(
        draftValues,
        beneficiarios.map((item, index) => mapProponentBeneficiarioFormToInclusionItem(item, index)),
      );
    } catch (error) {
      const parsedInconsistencies = isProponentInconsistencyError(error)
        ? error.inconsistencies
        : parseProponentInconsistenciesFromError(error);

      if (parsedInconsistencies.length > 0) {
        setInconsistencies(parsedInconsistencies);
        setIsInconsistencyModalOpen(true);
      }
    }
  };

  const handleCloseInconsistencyModal = () => {
    setIsInconsistencyModalOpen(false);
    setInconsistencies([]);

    if (draftValues) {
      setRestoreValues(draftValues);
      setFormSectionKey((key) => key + 1);
    }
  };

  const handleOpenBeneficiarios = () => {
    setBeneficiariosScreen('list');
  };

  const handleAddBeneficiario = () => {
    if (beneficiarioDraft && selectedBeneficiarioIndex == null) {
      setBeneficiariosScreen('detail');
      return;
    }
    setSelectedBeneficiarioIndex(null);
    setBeneficiarioDraft(createEmptyProponentBeneficiarioFormValues(
      draftValues?.matricula ?? '',
      String(beneficiarios.length + 1),
    ));
    setBeneficiariosScreen('detail');
  };

  const handleEditBeneficiario = (row: BeneficiarioTableItem) => {
    const index = beneficiariosRows.findIndex((item) => item.id === row.id);
    if (index < 0) {
      return;
    }
    const keepDraft = selectedBeneficiarioIndex === index && beneficiarioDraft
      ? beneficiarioDraft
      : beneficiarios[index];
    setSelectedBeneficiarioIndex(index);
    setBeneficiarioDraft(keepDraft);
    setBeneficiariosScreen('detail');
  };

  const handleSaveBeneficiario = () => {
    if (!beneficiarioDraft || !isBeneficiarioValid) {
      return;
    }

    setBeneficiarios((current) => {
      if (selectedBeneficiarioIndex == null) {
        return [...current, beneficiarioDraft];
      }
      return current.map((item, index) => (
        index === selectedBeneficiarioIndex ? beneficiarioDraft : item
      ));
    });
    setBeneficiariosScreen('list');
    setSelectedBeneficiarioIndex(null);
    setBeneficiarioDraft(null);
  };

  const handleWizardContinueOrSave = () => {
    if (isWizardLastSection) {
      void handleSave();
      return;
    }

    if (currentStep === 'beneficiarios') {
      if (isBeneficiariosDetailActive) {
        if (beneficiarioDraft && isBeneficiarioValid) {
          handleSaveBeneficiario();
        } else {
          setBeneficiariosScreen('list');
        }
      }
      return;
    }

    if (currentStep === 'insuranceInfo') {
      handleOpenBeneficiarios();
      return;
    }

    sectionControlRef.current?.goToNextSection();
  };

  return (
    <>
      <div className="bsds-mt-24" role="region" aria-label="Cadastro de novo proponente" aria-busy={isSaving}>
        <ProponentFormSection
          key={`new-proponent-${formKey}-${formSectionKey}`}
          initialValues={restoreValues}
          readOnly={isSaving}
          onValidityChange={setIsValid}
          onValuesChange={setDraftValues}
          showBeneficiariosButton
          isBeneficiariosActive={isBeneficiariosListActive || isBeneficiariosDetailActive}
          onBeneficiariosClick={handleOpenBeneficiarios}
          onExitBeneficiarios={() => resetBeneficiariosScreen({ clearDraft: false })}
          sectionControlRef={sectionControlRef}
          onActiveSectionChange={onActiveSectionChange}
        />

        {isBeneficiariosListActive && (
          <div className="bsds-mt-16" role="region" aria-label="Lista de beneficiários do proponente">
            <BeneficiariosTable
              rows={beneficiariosRows}
              onEdit={handleEditBeneficiario}
              onAdd={handleAddBeneficiario}
              canEdit
            />
          </div>
        )}

        {isBeneficiariosDetailActive && beneficiarioDraft && (
          <div className="bsds-mt-16">
            <ProponentBeneficiarioFormSection
              key={`beneficiario-${selectedBeneficiarioIndex ?? 'new'}-${formKey}`}
              initialValues={beneficiarioDraft}
              onValidityChange={setIsBeneficiarioValid}
              onValuesChange={setBeneficiarioDraft}
            />
          </div>
        )}

        <PersonFormActionButtons
          isLastSection={isWizardLastSection}
          isSaving={isSaving}
          canSave={isValid && draftValues != null}
          onPrimaryClick={handleWizardContinueOrSave}
          onCancel={onCancel}
          saveAriaLabel="Salvar cadastro de proponente"
          continueAriaLabel="Continuar cadastro de proponente"
          cancelAriaLabel="Cancelar cadastro de proponente"
          cancelColor="primary"
        />
      </div>

      <ProponentInconsistencyModal
        isOpen={isInconsistencyModalOpen}
        inconsistencies={inconsistencies}
        onClose={handleCloseInconsistencyModal}
      />
    </>
  );
};

export default NewProponentForm;
